import clsx from 'clsx'
import { FC, useEffect, useMemo, useState } from 'react'
import PlatformFieldDataSearchSelect from './PlatformFieldDataSearchSelect'
import InfoTooltip from './InfoTooltip'
import TimeRangePickerInput from './TimeRangePickerInput'
import {
  FIELD_MAPPING_DATA,
  SECTION_WISE_SAVED_FIELD_MAPPING,
} from 'src/interfaces/Platform.interface'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightArrowLeft, faDeleteLeft, faPlus } from '@fortawesome/free-solid-svg-icons'
import { useAppSelector } from 'src/redux/storeHooks'
import {useApplyPlatformSettingShortcodes} from 'src/utils/usePlatformSettingDisplayText'
import React from 'react'
import { usePermission } from 'src/hooks/usePermission'
import {formatDateForDateTimeLocal} from 'src/utils/formatDateForDateTimeLocal'
import {
  dedupeFieldMappingDataSet,
  hasMappingDatasetValue,
  mapDependentRowsToFieldMappingDataSet,
  matchesDependentFieldMappingSlot,
  resolveCanonicalPairFieldIds,
  resolveFieldMappingIsRequired,
} from 'src/utils/fieldMappingSaveUtils'

const DependentMapping: FC<{
  savedMappings: any
  selectedPlatformId: number
  isMappingVisible: boolean
  setSavedMappings: any
  handleChange: (e: React.ChangeEvent<any>) => void
  field_mapping: any
  savedMapping: SECTION_WISE_SAVED_FIELD_MAPPING | null | undefined
  settingName: string
  settingId: number | null
  setFieldMappings: React.Dispatch<React.SetStateAction<FIELD_MAPPING_DATA[]>>
  fieldMappings: FIELD_MAPPING_DATA[]
  sourcePlatform?: string
  destinationPlatform?: string
  index: number
  primary_settings: boolean
  group_code: string
  isMappedWith?: any
  platformCode?: string
  showUploadButton?: boolean
  onUploadClick?: () => void
  isRequiredMissing?: boolean
  /** Child settings on a secondary channel: keep API source (field_mapping) left → destination (map_with) right. */
  useGroup2ChildMappingDirection?: boolean
}> = ({
  field_mapping,
  isMappingVisible,
  settingId,
  settingName,
  setFieldMappings,
  fieldMappings,
  destinationPlatform,
  sourcePlatform,
  index,
  savedMappings,
  setSavedMappings,
  savedMapping,
  selectedPlatformId,
  primary_settings,
  group_code,
  isMappedWith,
  showUploadButton,
  onUploadClick,
  isRequiredMissing = false,
  useGroup2ChildMappingDirection = false,
}) => {
  const pickupFieldKey = `${field_mapping?.field_code ?? ''} ${field_mapping?.api_code ?? ''} ${field_mapping?.display_name ?? ''}`.toLowerCase()
  const isPickupTimeMapping =
    pickupFieldKey.includes('pickup_time') ||
    pickupFieldKey.includes('pickup time') ||
    pickupFieldKey.includes('pickup time range') ||
    pickupFieldKey.includes('24 hrs')
    const {isRead, isWrite, isDelete} = usePermission();

    const [errors, setErrors] = useState<Record<string, string>>({})
    const { fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform)
    const formatSettingDisplayText = useApplyPlatformSettingShortcodes()
    const [mapping, setMapping] = useState<any>([])
    const [isPairExist, setIsPairExist] = useState(false)
    const [pairedInputType, setPairedInputType] = useState('')
    const [errorIndex, setErrorIndex] = useState(0)
    const [selectedSide, setSelectedSide] = useState('')
    const [typedInputs, setTypedInputs] = useState<{ [key: string]: string }>({});
    const cloneMappingItem = (item: any) => (item ? JSON.parse(JSON.stringify(item)) : item)

    const isGroup1 = ['SHIPMENT', 'INVENTORY', 'PRICING', 'PRODUCT', 'INVOICE'].includes(group_code) // Primary --> Secondary flow
    const isGroup2 = [
      'ORDER',
      'ACCOUNTING_ORDER',
      'ERP_ORDER',
      'OMS_ORDER',
      'BACK_SHIPMENT',
      'ORDER_PAYOUT',
    ].includes(group_code) // Secondary --> Primary flow
    const swapGroup2PrimaryDisplay =
      isGroup2 && primary_settings && !useGroup2ChildMappingDirection

    const defaultSourceSide = useGroup2ChildMappingDirection
      ? field_mapping
      : (isGroup1 && primary_settings) || (isGroup2 && !primary_settings)
        ? field_mapping
        : isMappedWith

    const defaultDestinationSide = useGroup2ChildMappingDirection
      ? isMappedWith
      : (isGroup1 && primary_settings) || (isGroup2 && !primary_settings)
        ? isMappedWith
        : field_mapping

    const sourceSide = defaultSourceSide
    const destinationSide = defaultDestinationSide
    const invertRowDataFieldsForDisplay = swapGroup2PrimaryDisplay
    const displayLeftSide = sourceSide
    const displayRightSide = destinationSide

    const resolveDependentMappingIsRequired = () =>
      resolveFieldMappingIsRequired(field_mapping, {sourceSide, destinationSide})

    const isMappingRequired = resolveDependentMappingIsRequired()
    const isColumnRequired = (mappingSide: any) =>
      Boolean(mappingSide?.isRequired || isMappingRequired)

    const resolveRowDataField = (
      column: 'source' | 'destination'
    ): 'source_platform_data' | 'destination_platform_data' => {
      if (!invertRowDataFieldsForDisplay) {
        return column === 'source' ? 'source_platform_data' : 'destination_platform_data'
      }
      return column === 'source' ? 'destination_platform_data' : 'source_platform_data'
    }

    const resolveDisplayDataField = (
      column: 'left' | 'right'
    ): 'source_platform_data' | 'destination_platform_data' =>
      resolveRowDataField(column === 'left' ? 'source' : 'destination')

    const resolveSavedRowValueId = (
      entry: any,
      dataField: 'source_platform_data' | 'destination_platform_data'
    ) => {
      if (!entry) return null

      const nested = entry[dataField]
      if (nested !== null && nested !== undefined && nested !== '') {
        if (typeof nested === 'object' && nested?.id !== null && nested?.id !== undefined && nested?.id !== '') {
          return Number(nested.id)
        }
        if (typeof nested === 'number' || typeof nested === 'string') {
          const normalized = Number(nested)
          return Number.isNaN(normalized) ? null : normalized
        }
      }

      const idField = `${dataField}_id`
      const strField = `${dataField}_str`
      const candidate = entry[idField] ?? entry[strField]
      if (candidate === null || candidate === undefined || candidate === '') return null

      const normalized = Number(candidate)
      return Number.isNaN(normalized) ? candidate : normalized
    }

    const buildSelectOptions = (
      mappingSide: any,
      savedEntry: any,
      dataField: 'source_platform_data' | 'destination_platform_data'
    ) => {
      const optionMap = new Map<string, {value: any; label: string}>()

      ;(mappingSide?.platformfielddata_set || []).forEach((item: any) => {
        if (item?.id === null || item?.id === undefined || item?.id === '') return
        optionMap.set(String(item.id), {value: item.id, label: item.api_name})
      })

      const savedNested = savedEntry?.[dataField]
      if (
        savedNested &&
        typeof savedNested === 'object' &&
        savedNested?.id !== null &&
        savedNested?.id !== undefined &&
        savedNested?.id !== ''
      ) {
        const savedKey = String(savedNested.id)
        if (!optionMap.has(savedKey)) {
          optionMap.set(savedKey, {
            value: savedNested.id,
            label: savedNested.api_name || String(savedNested.id),
          })
        }
      }

      const savedOptionId = resolveSavedRowValueId(savedEntry, dataField)
      if (
        savedOptionId !== null &&
        savedOptionId !== undefined &&
        savedOptionId !== '' &&
        !optionMap.has(String(savedOptionId))
      ) {
        optionMap.set(String(savedOptionId), {
          value: savedOptionId,
          label: String(savedEntry?.[`${dataField}_str`] ?? savedOptionId),
        })
      }

      return Array.from(optionMap.values())
    }

    const effectiveSettingMappingId = useGroup2ChildMappingDirection
      ? field_mapping?.setting_mapping_id ??
        sourceSide?.setting_mapping_id ??
        destinationSide?.setting_mapping_id
      : (isGroup1 && !primary_settings) || (isGroup2 && primary_settings)
        ? destinationSide?.setting_mapping_id ??
          field_mapping?.setting_mapping_id ??
          sourceSide?.setting_mapping_id
        : sourceSide?.setting_mapping_id ??
          destinationSide?.setting_mapping_id ??
          field_mapping?.setting_mapping_id

    const getSideIdentifier = (side: any, prefix: string) => {
      const id = side?.id ?? side?.sourceFieldId ?? side?.destinationFieldId
      if (id !== undefined && id !== null) {
        return `${prefix}Id-${id}`
      }

      const code = side?.api_code ?? side?.field_code
      const platform = side?.platform ?? side?.source_field?.platform ?? side?.destination_field?.platform
      return `${prefix}Code-${code ?? 'null'}-${platform ?? 'platform-null'}`
    }

    const buildPairMappingKey = (source: any, destination: any, fallback?: string) => {
      if (source || destination) {
        return [
          'pair',
          getSideIdentifier(source, 'src'),
          getSideIdentifier(destination, 'dest'),
          `default-${Boolean(field_mapping?.isDefault)}`,
          `setting-${settingId ?? 'null'}`,
        ].join('-')
      }

      return fallback ?? `code-${field_mapping?.api_code ?? index}`
    }

    const mappingKey = useMemo(
      () =>
        buildPairMappingKey(
          sourceSide,
          destinationSide,
          field_mapping?.id
            ? `field-${field_mapping.id}`
            : effectiveSettingMappingId !== undefined && effectiveSettingMappingId !== null
            ? `setting-${effectiveSettingMappingId}`
            : savedMapping?.id
            ? `saved-${savedMapping.id}`
            : undefined
        ),
      [
        sourceSide?.id,
        sourceSide?.api_code,
        sourceSide?.field_code,
        sourceSide?.platform,
        destinationSide?.id,
        destinationSide?.api_code,
        destinationSide?.field_code,
        destinationSide?.platform,
        field_mapping?.isDefault,
        field_mapping?.id,
        effectiveSettingMappingId,
        field_mapping?.api_code,
        savedMapping?.id,
        settingId,
        index,
      ]
    )
    const getMappingIdentifier = (mappingItem: any): string => {
      if (!mappingItem) return ''
      if (mappingItem.mappingKey) {
        return mappingItem.mappingKey
      }
      if (
        (mappingItem.destinationFieldId !== undefined && mappingItem.destinationFieldId !== null) ||
        (mappingItem.sourceFieldId !== undefined && mappingItem.sourceFieldId !== null)
      ) {
        return buildPairMappingKey(
          {id: mappingItem.sourceFieldId},
          {id: mappingItem.destinationFieldId}
        )
      }
      if (mappingItem?.source_field || mappingItem?.destination_field) {
        return buildPairMappingKey(mappingItem.source_field, mappingItem.destination_field)
      }
      if (mappingItem.setting_mapping_id !== undefined && mappingItem.setting_mapping_id !== null) {
        return `setting-${mappingItem.setting_mapping_id}`
      }
      const destinationCode = mappingItem?.destination_field?.api_code ?? 'dest-null'
      const sourceCode = mappingItem?.source_field?.api_code ?? 'source-null'
      return `${destinationCode}-${sourceCode}`
    }
    const canonicalPairFieldIds = useMemo(
      () =>
        resolveCanonicalPairFieldIds(
          {
            savedMappingId: savedMapping?.id ?? null,
            setting_mapping_id: effectiveSettingMappingId,
            savedSettingId: settingId,
            sourceFieldId: sourceSide?.id ?? null,
            destinationFieldId: destinationSide?.id ?? null,
          },
          savedMappings
        ),
      [
        savedMapping?.id,
        effectiveSettingMappingId,
        settingId,
        sourceSide?.id,
        destinationSide?.id,
        savedMappings,
      ]
    )

    const matchesFieldMappingEntry = (item: any) => {
      if (!item || item.groupCode !== settingName) {
        return false
      }
      if (item.componentKey && item.componentKey === componentKey) {
        return true
      }

      if (
        field_mapping?.api_code &&
        item.fieldCode === field_mapping.api_code &&
        ((effectiveSettingMappingId != null &&
          item.setting_mapping_id === effectiveSettingMappingId) ||
          (savedMapping?.id != null && item.savedMappingId === savedMapping.id))
      ) {
        return true
      }

      const itemIdentifier = getMappingIdentifier(item)
      const targetIdentifier =
        mappingKey ||
        getMappingIdentifier({
          mappingKey,
          destination_field: destinationSide,
          source_field: sourceSide,
          setting_mapping_id: effectiveSettingMappingId,
        })
      return itemIdentifier === targetIdentifier
    }

    const componentKey = useMemo(
      () =>
        [
          'dep',
          group_code ?? 'group',
          settingName ?? 'setting',
          effectiveSettingMappingId ?? field_mapping?.id ?? field_mapping?.api_code ?? 'field',
          sourceSide?.id ?? 's',
          destinationSide?.id ?? 'd',
          index,
        ].join('-'),
      [
        group_code,
        settingName,
        effectiveSettingMappingId,
        field_mapping?.id,
        field_mapping?.api_code,
        sourceSide?.id,
        destinationSide?.id,
        index,
      ]
    )
    const buildControlToken = (
      side: 'source_platform_data' | 'destination_platform_data',
      rowIndex: number,
      suffix = 'input'
    ) =>
      `${componentKey}-${side}-${rowIndex}-${suffix}`

   const resolvePlatformNameFromId = (platformId?: number | string | null) => {
    if (platformId === null || platformId === undefined || platformId === '') {
      return ''
    }

    const normalizedPlatformId = Number(platformId)
    const matchedPlatform = (fetchedConnectedPlatformData || []).find(
      (item: any) => Number(item?.id) === normalizedPlatformId
    )

    return (
      matchedPlatform?.platform_info?.display_name ||
      matchedPlatform?.display_name ||
      ''
    )
   }

   const resolvePlatformNameFromField = (field: any, fallbackName?: string) => {
    const platformIdCandidates = [
      field?.platform,
      field?.map_with?.platform,
      field?.source_field?.platform,
      field?.destination_field?.platform,
      field_mapping?.map_with?.platform,
      isMappedWith?.platform,
    ]

    const resolvedName =
      platformIdCandidates
        .map((platformId) => resolvePlatformNameFromId(platformId))
        .find(Boolean) || ''

    return resolvedName || fallbackName || ''
   }

   const sourcePlatformName =
    resolvePlatformNameFromField(sourceSide, sourcePlatform || destinationPlatform)

   const destinationPlatformName =
    resolvePlatformNameFromField(destinationSide, destinationPlatform || sourcePlatform)
   const shouldRenderUploadButton = Boolean(showUploadButton)

    useEffect(() => {
      let normalizedMapping: any[] | null = null

      const hydrateMappingState = (mappingRecord: any) => {
        const cloned = mappingRecord.map(cloneMappingItem)
        if (!cloned[0]?.fieldmappingdata_set?.length) {
          cloned[0].fieldmappingdata_set = [
            {
              id: null,
              destination_platform_data: '',
              destination_platform_data_str: '',
              source_platform_data: '',
              source_platform_data_str: '',
              isDefault: field_mapping?.isDefault,
            },
          ]
        } else {
          cloned[0].fieldmappingdata_set = dedupeFieldMappingDataSet(
            cloned[0].fieldmappingdata_set
          )
        }
        cloned[0].mappingKey = cloned[0].mappingKey ?? mappingKey
        normalizedMapping = cloned
        setMapping(cloned)
      }

      const matchingSavedRecords =
        savedMappings?.filter((mapping: any) => {
          if (mapping?.id && savedMapping?.id && mapping?.id === savedMapping?.id) {
            return true
          }

          return matchesDependentFieldMappingSlot(mapping, {
            sourceSide,
            destinationSide,
            effectiveSettingMappingId,
            settingId,
            isDefault: field_mapping?.isDefault,
          })
        }) ?? []

      const liveSavedMapping = matchingSavedRecords[0] ?? null

      if (liveSavedMapping?.fieldmappingdata_set?.length) {
        hydrateMappingState([liveSavedMapping])
      } else if (savedMapping?.fieldmappingdata_set?.length) {
        hydrateMappingState([savedMapping])
      }

      if (!normalizedMapping) {
        const defaultMapping = [
          {
            id: savedMapping?.id ?? null,
            setting_id: settingId,
            isDefault: field_mapping?.isDefault,
            source_field: sourceSide,
            destination_field: destinationSide,
            setting_mapping_id: effectiveSettingMappingId,
            fieldmappingdata_set: [
              {
                id: null,
                destination_platform_data: '',
                destination_platform_data_str: '',
                source_platform_data: '',
                source_platform_data_str: '',
                isDefault: field_mapping?.isDefault,
              },
            ],
            mappingKey,
          },
        ]
        normalizedMapping = defaultMapping.map(cloneMappingItem)
        setMapping(normalizedMapping)
      }

      if (normalizedMapping && normalizedMapping.length > 0) {
        const field_mapping_data_set = mapDependentRowsToFieldMappingDataSet(
          normalizedMapping[0]?.fieldmappingdata_set || [],
          field_mapping?.isDefault
        )

        const shouldTrackFieldMapping = Boolean(
          isMappedWith &&
            ((primary_settings === true && field_mapping?.map_with != null) ||
              primary_settings === false ||
              resolveDependentMappingIsRequired())
        )

        if (shouldTrackFieldMapping) {
          const hasExistingMappingRef = Boolean(
            normalizedMapping?.[0]?.id ?? savedMapping?.id
          )
          const canPersistFieldMapping =
            canonicalPairFieldIds.destinationFieldId != null ||
            hasExistingMappingRef ||
            hasMappingDatasetValue(field_mapping_data_set, {
              sourceFieldId: canonicalPairFieldIds.sourceFieldId,
              destinationFieldId: canonicalPairFieldIds.destinationFieldId,
            })

          if (!canPersistFieldMapping) {
            setFieldMappings((prev) => {
              const index = prev.findIndex(matchesFieldMappingEntry)
              if (index === -1) return prev
              const updated = [...prev]
              updated.splice(index, 1)
              return updated
            })
            return
          }

          setFieldMappings((prev) => {
            const index = prev.findIndex(matchesFieldMappingEntry)

            const updatedObject = {
              field_mapping_id: normalizedMapping?.[0]?.id ?? null,
              groupCode: settingName,
              fieldCode: field_mapping.api_code,
              savedSettingId: settingId,
              isDefault: field_mapping?.isDefault,
              isRequired: resolveDependentMappingIsRequired(),
              savedMappingId: savedMapping?.id ?? null,
              setting_mapping_id: effectiveSettingMappingId,
              destinationFieldId: canonicalPairFieldIds.destinationFieldId,
              sourceFieldId: canonicalPairFieldIds.sourceFieldId,
              fieldMappingDataSet: field_mapping_data_set || [],
              mappingKey,
              componentKey,
            }

            if (index !== -1) {
              const existing = prev[index]
              if (
                existing.destinationFieldId === updatedObject.destinationFieldId &&
                existing.sourceFieldId === updatedObject.sourceFieldId &&
                JSON.stringify(existing.fieldMappingDataSet) ===
                  JSON.stringify(updatedObject.fieldMappingDataSet) &&
                existing.mappingKey === updatedObject.mappingKey
              ) {
                return prev
              }

              const updated = [...prev]
              updated[index] = {
                ...existing,
                ...updatedObject,
              }
              return updated
            }

            return [...prev, updatedObject]
          })
        }
      }
    }, [
      savedMappings,
      savedMapping,
      savedMapping?.id,
      effectiveSettingMappingId,
      settingId,
      sourceSide?.id,
      sourceSide?.api_code,
      sourceSide?.platform,
      destinationSide?.id,
      destinationSide?.api_code,
      destinationSide?.platform,
      field_mapping?.isDefault,
      mappingKey,
    ])


    const updateSavedMappings = (updatedMapping: any) => {
      setSavedMappings((prevMappings) => {
        const newMappings = [...prevMappings]
        updatedMapping.forEach((mappingItem) => {
          if (!mappingItem) {
            return
          }

          const identifier = getMappingIdentifier({
            ...mappingItem,
            mappingKey: mappingItem.mappingKey ?? mappingKey,
          })

          const clonedItem = cloneMappingItem(mappingItem) || {}
          clonedItem.mappingKey = identifier
          clonedItem.componentKey = componentKey

          if (mappingItem.id === null || mappingItem.id === undefined) {
            const existingIndex = newMappings.findIndex(
              (candidate) =>
                getMappingIdentifier(candidate) === identifier ||
                candidate?.componentKey === componentKey
            )

            if (existingIndex === -1) {
              newMappings.push(clonedItem)
            } else {
              newMappings[existingIndex] = clonedItem
            }
          } else {
            // Prioritize matching by id to avoid componentKey collisions
            // when multiple saved entries share the same setting_mapping_id
            let existingIndex = newMappings.findIndex(
              (candidate) => candidate.id === mappingItem.id
            )
            if (existingIndex === -1) {
              existingIndex = newMappings.findIndex(
                (candidate) => candidate?.componentKey === componentKey
              )
            }

            if (existingIndex === -1) {
              newMappings.push(clonedItem)
            } else {
              newMappings[existingIndex] = clonedItem
            }
          }
        })
        return newMappings
      })
    }
    const validateField = (fieldName: string, value: any, isRequired: boolean) => {
      setErrors((prevErrors) => {
        const fieldKey = `${index}_${fieldName}`
        if (isRequired && (!value || (Array.isArray(value) && value.length === 0))) {
          return { ...prevErrors, [fieldKey]: 'This field is required' }
        } else {
          const { [fieldKey]: _, ...newErrors } = prevErrors
          return newErrors
        }
      })
    }

    const formatDateString = (dateString: string) =>
      formatDateForDateTimeLocal(dateString)
    const handleInputChange = (
      value: any,
      field: string,
      dataField: string,
      isRequired: boolean,
      mapping_id,
      mappingId: number,
      targetIndex: number,
      mappingSide: any,
      selectedOptionData?: any
    ) => {
      validateField(field, value, isRequired)
      const updatedMapping = mapping.map((item: any) => {
        const otherSide = field === 'source_platform_data' ? 'destination_platform_data' : 'source_platform_data'
        let updatedFieldMappingDataSet
        if (mappingSide.input_type === 'SELECT') {
          updatedFieldMappingDataSet = item.fieldmappingdata_set.map((fm: any, index: number) => {
            if (index === targetIndex) {
              const hasPair = (value === 0 || value === '' || value === null || value === undefined)
                ? false
                : !!(fm[`${otherSide}`] || fm[`${otherSide}_str`]);
              const selectedOption =
                (selectedOptionData ??
                mappingSide.platformfielddata_set.find((p) => p.id === value)) ||
                null

              return {
                ...fm,
                [field]: selectedOption,
                [`${field}_str`]: selectedOption?.id ?? null,
                hasPair,
              };
            }
            return fm;
          });
        } else if (mappingSide.input_type === 'MULTISELECT') {
          const idKey = field === 'source_platform_data' ? 'source_platform_data_id' : 'destination_platform_data_id'
          const strKey = field === 'source_platform_data' ? 'source_platform_data_str' : 'destination_platform_data_str'
          const selectedValues: any[] = Array.isArray(value) ? value : value ? [value] : []
          const selectedId = selectedValues.length ? selectedValues[selectedValues.length - 1] : null
          const selectedOption =
            selectedId !== null
              ? (Array.isArray(selectedOptionData)
                  ? selectedOptionData[selectedValues.length - 1]
                  : selectedOptionData) ??
                mappingSide.platformfielddata_set.find((itm: any) => itm.id === Number(selectedId)) ??
                null
              : null

          updatedFieldMappingDataSet = item.fieldmappingdata_set.map((fm: any, idx: number) => {
            if (idx !== targetIndex) return fm
            const otherSide = field === 'source_platform_data' ? 'destination_platform_data' : 'source_platform_data'
            const hasPair =
              selectedId === null || selectedId === 0 || selectedId === ''
                ? false
                : !!(fm[`${otherSide}`] || fm[`${otherSide}_str`])

            return {
              ...fm,
              [field]: selectedOption,
              [idKey]: selectedId,
              [strKey]: selectedId,
              hasPair,
            }
          })
        } else {
          updatedFieldMappingDataSet = item.fieldmappingdata_set.map((fm: any, index: number) =>
            index === targetIndex
              ? {
                ...fm,
                [dataField]: value || null,
                hasPair:
                  (value === 0 || value === '')
                    ? false
                    : !!(fm[`${otherSide}`] || fm[`${otherSide}_str`]),
              }
              : fm
          );
        }
        return { ...item, fieldmappingdata_set: updatedFieldMappingDataSet }
      })
      
      updateSavedMappings(updatedMapping)
      let updatedFieldMappings: any = [...fieldMappings]
      const selectedValues =
        value === null || value === undefined || value === ''
          ? []
          : Array.isArray(value)
          ? value
          : [value]
      let existingFieldMappingIndex = updatedFieldMappings.findIndex(matchesFieldMappingEntry)
      let existingFieldMapping: any =
        existingFieldMappingIndex !== -1 ? updatedFieldMappings[existingFieldMappingIndex] : null

      const shouldCreateEntry =
        !existingFieldMapping &&
        selectedValues.some((val) => !(val === 0 || val === '' || val === null || val === undefined))

      if (shouldCreateEntry) {
        const newEntry = {
          field_mapping_id: mapping[0]?.id,
          setting_mapping_id: effectiveSettingMappingId,
          groupCode: settingName,
          fieldCode: field_mapping.api_code,
          savedSettingId: settingId,
          isDefault: field_mapping?.isDefault,
          isRequired: resolveDependentMappingIsRequired(),
          savedMappingId: savedMapping?.id ?? null,
          destinationFieldId: canonicalPairFieldIds.destinationFieldId,
          sourceFieldId: canonicalPairFieldIds.sourceFieldId,
          fieldMappingDataSet: [],
          mappingKey,
          componentKey,
        }
        updatedFieldMappings.push(newEntry)
        existingFieldMappingIndex = updatedFieldMappings.length - 1
        existingFieldMapping = newEntry
      }

      if (!existingFieldMapping) {
        setFieldMappings(updatedFieldMappings)
        return
      }

      existingFieldMapping.mappingKey = mappingKey

      if (field_mapping.input_type === 'MULTISELECT') {
        const selectedArray = Array.isArray(value) ? value : value ? [value] : []
        const selectedId =
          selectedArray.filter((val) => val !== 0 && val !== '' && val !== null && val !== undefined).pop() ?? null
        const idField = dataField
        const baseField = field
        existingFieldMapping.fieldMappingDataSet[targetIndex] = {
          ...(existingFieldMapping.fieldMappingDataSet[targetIndex] || {}),
          id: mappingId ?? null,
          [idField]: selectedId,
          [`${baseField}_str`]: selectedId,
          isDefault: field_mapping?.isDefault,
        }
        setIsPairExist(false)
      } else {
        existingFieldMapping.fieldMappingDataSet = mapDependentRowsToFieldMappingDataSet(
          updatedMapping[0]?.fieldmappingdata_set || [],
          field_mapping?.isDefault
        )
        existingFieldMapping.isRequired = resolveDependentMappingIsRequired()

        const clearedRow = existingFieldMapping.fieldMappingDataSet[targetIndex]
        if (
          value === 0 ||
          value === '' ||
          value === null ||
          value === undefined
        ) {
          const {
            source_platform_data_id,
            source_platform_data_str,
            destination_platform_data_id,
            destination_platform_data_str,
          } = clearedRow || {}
          if (
            source_platform_data_id ||
            source_platform_data_str ||
            destination_platform_data_id ||
            destination_platform_data_str
          ) {
            setSelectedSide(field)
            setPairedInputType(mappingSide?.input_type)
            setIsPairExist(true)
            setErrorIndex(targetIndex)
          }
        } else {
          setIsPairExist(false)
        }
      }

      updatedFieldMappings[existingFieldMappingIndex] = existingFieldMapping
      setFieldMappings(updatedFieldMappings)
      setMapping(updatedMapping.map(cloneMappingItem))
    }
    const renderInputField = (
      dataField: 'source_platform_data' | 'destination_platform_data',
      index: number,
      mappingSide: any
    ) => {
      const errorSide = dataField === 'source_platform_data' ? 'Source' : 'Destination'
      const fieldKey = buildControlToken(dataField, index, 'typed')
      const controlId = buildControlToken(dataField, index)
      const savedRowEntry = mapping[0]?.fieldmappingdata_set?.[index]
      const savedValue =
        mappingSide.input_type === 'DATETIME'
          ? formatDateString(savedRowEntry?.[`${dataField}_str`] ?? '')
          : savedRowEntry?.[`${dataField}_str`] ?? '';
    
      const inputValue = typedInputs[fieldKey] ?? savedValue;
      const savedOptionId = resolveSavedRowValueId(savedRowEntry, dataField)
      const selectOptions = buildSelectOptions(mappingSide, savedRowEntry, dataField)

      switch (mappingSide?.input_type) {
        case 'SELECT':
          return (
            <>
              <PlatformFieldDataSearchSelect
                field={mappingSide}
                className="react-select"
                classNamePrefix="select"
                inputId={controlId}
                instanceId={controlId}
                isSearchable
                options={selectOptions}

                value={
                  savedOptionId === null
                    ? null
                    : selectOptions.find(
                        (option) =>
                          Number(option.value) === Number(savedOptionId) ||
                          option.value === savedOptionId
                      ) ?? null
                }

                onChange={(selectedOption) =>
                  handleInputChange(
                    selectedOption?.value ?? null,
                    dataField,
                    `${dataField}_id`,
                    mappingSide?.isRequired,
                    mapping[0]?.id,
                    mapping[0].fieldmappingdata_set[0].id,
                    index,
                    mappingSide,
                    selectedOption?.raw
                  )
                }
                 styles={{
              menu: (base) => ({
                ...base,
                zIndex: 10,       
                position: "absolute", 
              }),
              menuList: (base) => ({
                ...base,
                zIndex: 10,
              }),
            }}
              />

              {dataField === selectedSide &&
                index === errorIndex &&
                'SELECT' === pairedInputType &&
                isPairExist && (
                  <small className='text-danger'>{errorSide} is required</small>
                )}
            </>
          )

        case 'MULTISELECT':
          const currentIds = (() => {
            const entry = mapping[0]?.fieldmappingdata_set?.[index]
            if (!entry) return []
            const idField = `${dataField}_id`
            const dataVal = entry[idField] ?? entry[dataField]
            if (Array.isArray(dataVal)) {
              return dataVal
                .map((v: any) => (v && v.id ? v.id : v))
                .filter((v: any) => v !== null && v !== undefined)
            }
            if (dataVal?.id) return [dataVal.id]
            const strVal = entry[`${dataField}_str`] ?? entry[idField]
            if (Array.isArray(strVal)) return strVal.filter((v: any) => v !== null && v !== undefined)
            if (strVal !== undefined && strVal !== null && strVal !== '') return [Number(strVal) || strVal]
            return []
          })()

          return (
            <PlatformFieldDataSearchSelect
              field={mappingSide}
              isMulti
              inputId={controlId}
              instanceId={controlId}
              options={mappingSide?.platformfielddata_set?.slice(0, 100)?.map((item: any) => ({
                value: item.id,
                label: item.api_name,
              }))}
              value={currentIds
                .map((selectedId: any) => {
                  const selectedEntry = mapping[0]?.fieldmappingdata_set?.[index]?.[dataField]
                  if (
                    typeof selectedEntry === 'object' &&
                    selectedEntry?.id &&
                    Number(selectedEntry.id) === Number(selectedId)
                  ) {
                    return {
                      value: selectedEntry.id,
                      label: selectedEntry.api_name ?? selectedEntry.display_name ?? String(selectedEntry.id),
                      raw: selectedEntry,
                    }
                  }

                  const matchedOption = mappingSide?.platformfielddata_set?.find(
                    (opt: any) => Number(opt.id) === Number(selectedId)
                  )

                  return matchedOption
                    ? {value: matchedOption.id, label: matchedOption.api_name, raw: matchedOption}
                    : null
                })
                .filter(Boolean)}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions?.map((opt: any) => opt.value) || []
                handleInputChange(
                  selectedValues,
                  dataField,
                  `${dataField}_id`,
                  mappingSide?.isRequired,
                  mapping[0]?.id,
                  mapping[0].fieldmappingdata_set[0].id,
                  index,
                  mappingSide,
                  selectedOptions?.map((opt: any) => opt.raw)
                )
              }}
            />
          )

        case 'BOOLEAN':
          return (
            <div className='form-check form-switch form-check-solid'>
              <input
                id={controlId}
                type='checkbox'
                className='form-check-input h-30px w-50px'
                checked={mapping[0]?.fieldmappingdata_set[0]?.[`${dataField}_str`] === 'true'}
                required={mappingSide?.isRequired || false}
                onChange={(e) =>
                  handleInputChange(
                    e.target.checked ? 'true' : 'false',
                    dataField,
                    `${dataField}_str`,
                    mappingSide?.isRequired,
                    mapping[0]?.id,
                    mapping[0].fieldmappingdata_set[0].id,
                    index,
                    mappingSide
                  )
                }
              />
            </div>
          )

        case 'DATETIME':
        case 'INTEGER':
        case 'TEXTAREA':
        case 'TEXT':
          return isPickupTimeMapping && mappingSide.input_type === 'TEXT' && dataField === 'destination_platform_data' ? (
            <>
              <TimeRangePickerInput
                label={field_mapping.display_name}
                message={field_mapping?.description ?? ''}
                hideLabel
                containerClasses=''
                name={`${dataField}_str`}
                id={`dep-pickup-${index}`}
                value={inputValue ?? ''}
                required={isColumnRequired(mappingSide)}
                onChange={(e) => {
                  const newVal = e.target.value
                  setTypedInputs((prev) => ({ ...prev, [fieldKey]: newVal }))

                  handleInputChange(
                    newVal,
                    dataField,
                    `${dataField}_str`,
                    mappingSide?.isRequired,
                    mapping[0]?.id,
                    mapping[0].fieldmappingdata_set[0].id,
                    index,
                    mappingSide
                  )
                }}
              />
              {dataField === selectedSide && index === errorIndex && 'TEXT' === pairedInputType && isPairExist && (
                <small className='text-danger'>{errorSide} is required</small>
              )}
            </>
          ) : (
            <>
              <input
                id={controlId}
                type={
                  mappingSide.input_type === 'DATETIME'
                    ? 'datetime-local'
                    : mappingSide.input_type === 'INTEGER'
                      ? 'number'
                      : 'text'
                }
                placeholder={
                  mappingSide.input_type === 'TEXTAREA' ? 'Add a description' : 'Enter value'
                }
                className={`form-control form-control-lg ${errors[`${index}_destination_platform_data_str`] ? 'border border-danger' : ''
                  }`}
                  value={inputValue}
                  required={isColumnRequired(mappingSide)}
                  onChange={(e) => {
                    const newVal = e.target.value;
                    setTypedInputs((prev) => ({ ...prev, [fieldKey]: newVal }));
          
                    handleInputChange(
                      newVal,
                     dataField,
                    `${dataField}_str`,
                    mappingSide?.isRequired,
                    mapping[0]?.id,
                    mapping[0].fieldmappingdata_set[0].id,
                    index,
                    mappingSide
                  )
                }}
              />
              {dataField === selectedSide && index === errorIndex && 'TEXT' === pairedInputType && isPairExist && (
                <small className='text-danger'>{errorSide} is required</small>
              )}
            </>
          )

        default:
          return null
      }
    }

    const handleAddMapping = () => {
      const newMapping = {
        id: mapping[0].id,
        setting_id: mapping[0].setting_id,
        source_field: mapping[0]?.source_field,
        destination_field: mapping[0]?.destination_field,
        setting_mapping_id: mapping[0]?.setting_mapping_id ?? effectiveSettingMappingId,
        isDefault: mapping[0]?.isDefault,
        mappingKey,
        fieldmappingdata_set: [
          ...mapping[0]?.fieldmappingdata_set,
          {
            id: null,
            destination_platform_data: '',
            destination_platform_data_str: '',
            isDefault: mapping[0]?.isDefault,
            hasPair: false,
            source_platform_data: '',
            source_platform_data_str: '',
          },
        ],
      }
      const updatedMapping = [newMapping]
      updateSavedMappings(updatedMapping)
      setMapping(updatedMapping.map(cloneMappingItem))

      const updatedFieldMappings = [...fieldMappings]
      const existingFieldMappingIndex = updatedFieldMappings.findIndex(matchesFieldMappingEntry)

      if (existingFieldMappingIndex !== -1) {
        const existingFieldMapping = {
          ...updatedFieldMappings[existingFieldMappingIndex],
        }
        existingFieldMapping.mappingKey = mappingKey
        existingFieldMapping.fieldMappingDataSet = mapDependentRowsToFieldMappingDataSet(
          updatedMapping[0]?.fieldmappingdata_set || [],
          field_mapping?.isDefault
        )
        updatedFieldMappings[existingFieldMappingIndex] = existingFieldMapping
        setFieldMappings(updatedFieldMappings)
      }
    }

    const handleDeleteMapping = (targetIndex) => {
      let updatedMapping = mapping.map((mapItem) => ({
        ...mapItem,
        fieldmappingdata_set: mapItem.fieldmappingdata_set.filter(
          (_, index) => index !== targetIndex
        ),
      }))

      updateSavedMappings(updatedMapping)
      setMapping(updatedMapping.map(cloneMappingItem))

      let updatedFieldMappings = [...fieldMappings]
      const targetMappingIndex = updatedFieldMappings.findIndex(matchesFieldMappingEntry)

      if (targetMappingIndex !== -1) {
        const existingFieldMapping: any = updatedFieldMappings[targetMappingIndex]
        existingFieldMapping.mappingKey = mappingKey
        existingFieldMapping?.fieldMappingDataSet?.splice(targetIndex, 1)
        updatedFieldMappings[targetMappingIndex] = existingFieldMapping
        setFieldMappings(updatedFieldMappings)
      }
    }
    return (
      <>
        {isMappedWith && (
          <div>
            <div className={clsx('row mb-2')}>
              <div className={clsx('mapping', isRequiredMissing && 'mapping-required-missing')}>
                <div className='p-2'>
                  <div>
                    <label className={`form-label me-2 ${isMappingRequired ? 'required' : ''}`}>
                      {formatSettingDisplayText(displayLeftSide?.display_name)} (
                      {sourcePlatformName}) to{' '}
                      {formatSettingDisplayText(displayRightSide?.display_name)} (
                      {destinationPlatformName}) mapping
                    </label>
                    {field_mapping?.description && (
                      <InfoTooltip message={field_mapping?.description} />
                    )}
                    {shouldRenderUploadButton && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary ms-3'
                        onClick={onUploadClick}
                      >
                        <i className='bi bi-upload me-1'></i>
                        Upload
                      </button>
                    )}
                  </div>
                  <div className='border-bottom my-2 border-2 my-2'></div>
                  <div key={'mapping' + index + mapping?.id + ''} className={`mb-sm-0 mb-2 p-2`}>
                    <div className='row'>
                      {mapping?.length &&
                        mapping[0]?.fieldmappingdata_set?.map((item, mappingFieldindex: number) => (
                          <React.Fragment
                            key={buildControlToken(
                              'source_platform_data',
                              mappingFieldindex,
                              `fragment-${item?.id ?? 'new'}`
                            )}
                          >
                            <div className='col-12 col-sm-5 pe-0 py-0'>
                              <div>
                                <label
                                  htmlFor={buildControlToken(
                                    'source_platform_data',
                                    mappingFieldindex
                                  )}
                                  className={`form-check-label mb-1 ${isColumnRequired(displayLeftSide) ? 'required' : ''
                                    }`}
                                >
                                  {formatSettingDisplayText(displayLeftSide?.display_name)} from{' '}
                                  {sourcePlatformName}
                                </label>

                                {renderInputField(
                                  resolveDisplayDataField('left'),
                                  mappingFieldindex,
                                  displayLeftSide
                                )}
                              </div>
                            </div>
                            <div className='col-12 col-sm-1 my-1 my-sm-0 p-0'>
                              <div className='d-flex justify-content-center align-items-center h-100'>
                                <FontAwesomeIcon icon={faArrowRightArrowLeft} className='mt-6' />
                              </div>
                            </div>
                            <div className='col-12 col-sm-5 mb-2 p-0'>
                              <div>
                                <label
                                  htmlFor={buildControlToken(
                                    'destination_platform_data',
                                    mappingFieldindex
                                  )}
                                  className={`form-check-label mb-1 ${isColumnRequired(displayRightSide) ? 'required' : ''
                                    }`}
                                >
                                  {formatSettingDisplayText(displayRightSide?.display_name)} from{' '}
                                  {destinationPlatformName}
                                </label>
                                {renderInputField(
                                  resolveDisplayDataField('right'),
                                  mappingFieldindex,
                                  displayRightSide
                                )}
                              </div>
                            </div>
                            {mappingFieldindex != 0 ? (
                              <>
                                <div className='align-content-end col-12 col-sm-1'>
                                  <div className='d-flex justify-content-end'>
                                    {isDelete && (<button
                                      type='button'
                                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        event.preventDefault()
                                        handleDeleteMapping(mappingFieldindex)
                                      }}
                                      className='btn btn-sm mt-2'
                                      style={{ color: 'red' }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faDeleteLeft}
                                        style={{ fontSize: '20px' }}
                                      />
                                    </button> )}
                                  </div>
                                </div>
                              </>
                            ) : null}{' '}
                        </React.Fragment>
                        ))}
                      {
                        <div className='d-flex justify-content-end'>
                          <button
                            className='btn btn-primary btn-sm me-3'
                            type='button'
                            onClick={handleAddMapping}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>{' '}
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

export default DependentMapping
